import { useCallback, useEffect, useRef, useState } from 'react'

import { Icon } from '../Icon'

/**
 * Поле ввода сообщения.
 *
 * Textarea растёт с текстом до пяти строк, дальше — прокрутка.
 * Отправка очищает поле СРАЗУ: сообщение уходит в офлайн-очередь
 * и появляется в ленте оптимистично, задерживать ввод сетью нельзя.
 *
 * Клавиатура: на десктопе Enter отправляет, Shift+Enter — перенос
 * (submitOnEnter=true, консоль); на телефоне Enter всегда перенос,
 * отправка — только кнопкой (submitOnEnter=false, драйвер):
 * экранная клавиатура не различает Shift.
 *
 * Состояния: disabled (нет сети и очередь переполнена, чат закрыт),
 * его причина — плейсхолдером.
 */

const MAX_ROWS = 5
const LINE_PX = 22

export function MessageComposer({
  onSend,                    // ({ body }) => void — постановка в очередь
  onAttach,                  // открыть выбор вложений (bottom sheet / файл)
  disabled = false,
  disabledHint = 'Отправка недоступна',
  placeholder = 'Сообщение',
  submitOnEnter = false,
  autoFocus = false,
}) {
  const [value, setValue] = useState('')
  const areaRef = useRef(null)

  const resize = useCallback(() => {
    const el = areaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_ROWS * LINE_PX + 16)}px`
  }, [])

  useEffect(() => {
    resize()
  }, [value, resize])

  const send = useCallback(() => {
    const body = value.trim()
    if (!body || disabled) return
    setValue('')
    onSend?.({ body })
    areaRef.current?.focus()
  }, [value, disabled, onSend])

  const onKeyDown = (e) => {
    if (submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const canSend = !disabled && value.trim().length > 0

  return (
    <div className={`composer ${disabled ? 'composer--disabled' : ''}`}>
      {onAttach && (
        <button
          type="button"
          className="composer__attach"
          onClick={onAttach}
          disabled={disabled}
          aria-label="Прикрепить файл"
        >
          <Icon name="paperclip" size={22} />
        </button>
      )}

      <textarea
        ref={areaRef}
        className="composer__input"
        rows={1}
        value={value}
        placeholder={disabled ? disabledHint : placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Текст сообщения"
      />

      <button
        type="button"
        className="composer__send"
        onClick={send}
        disabled={!canSend}
        aria-label="Отправить"
      >
        <Icon name="send" size={20} />
      </button>
    </div>
  )
}
